'use client';
import { IdentityListType } from '../types';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import QueryKey from '../utils/queryKeys';
import { gql } from '@urql/core';
import { useUrqlClient } from './useUrqlClient';

type QueryResponse = {
  shinkaiIdentities: IdentityListType[];
};

// Purposefully not inlined in the queries to keep them static
const pageSize = 10;

// For all identities: use skip-based pagination to allow proper sorting by stakedTokens
const AllIdentitiesQuery = gql<QueryResponse, { filter: string; skip: number }>`
  query ($filter: String!, $skip: Int!) {
    shinkaiIdentities(
      first: 10
      skip: $skip
      orderBy: stakedTokens
      orderDirection: desc
      where: { identityRaw_contains_nocase: $filter }
    ) {
      identityRaw
      stakedTokens
    }
  }
`;

// For user identities: cursor-based pagination works fine with alphabetical order
const UserIdentitiesQuery = gql<
  QueryResponse,
  { filter: string; pageParam: string; owner: string }
>`
  query ($filter: String!, $pageParam: String!, $owner: String!) {
    shinkaiIdentities(
      first: 10
      orderBy: identityRaw
      where: {
        and: [
          { identityRaw_contains_nocase: $filter }
          { identityRaw_gt: $pageParam }
          { nft_: { owner: $owner } }
        ]
      }
    ) {
      identityRaw
      stakedTokens
    }
  }
`;

export const useGetIdentities = ({
  filter,
  owner,
}: {
  filter?: string;
  owner?: string;
}) => {
  const { data: urqlData } = useUrqlClient();
  filter = filter?.trim() ?? '';

  const urqlClient = urqlData?.client;

  return useInfiniteQuery({
    queryKey: [QueryKey.IDENTITIES, { filter, owner }],
    queryFn: async ({ pageParam }) => {
      if (!urqlClient) return [];

      if (owner) {
        // User identities: cursor-based pagination
        const result = await urqlClient.query(UserIdentitiesQuery, {
          filter,
          pageParam: pageParam as string,
          owner,
        });
        return result.data?.shinkaiIdentities ?? [];
      } else {
        // All identities: skip-based pagination for proper sorting
        const result = await urqlClient.query(AllIdentitiesQuery, {
          filter,
          skip: pageParam as number,
        });
        return result.data?.shinkaiIdentities ?? [];
      }
    },
    initialPageParam: owner ? '' : 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length < pageSize) {
        return null;
      }

      if (owner) {
        // Cursor-based: return last identity name
        return lastPage[lastPage.length - 1]?.identityRaw ?? null;
      } else {
        // Skip-based: return total count so far
        const totalFetched = allPages.reduce(
          (sum, page) => sum + (page?.length ?? 0),
          0,
        );
        return totalFetched;
      }
    },
    enabled: !!urqlClient,
    placeholderData: keepPreviousData,
    staleTime: 60000,
  });
};
